import { action, makeObservable, observable, runInAction } from 'mobx';
import { setupMobX } from '../util/setupMobX';
import {
  clusteringData,
  exportOnboardingData,
  exportParticipants,
  phoneTrackingData,
  studyData,
} from '../request/authenticated-requests/export';
import { CohortType } from '@prisma/client';

export enum ExportType {
  Onboarding = 'onboarding',
  Clustering = 'clustering',
  AllStudyData = 'allStudyData',
  PhoneTracking = 'phoneTracking',
  Participants = 'participants',
}

export class ExportStore {
  @observable public open = false;

  @observable public exportType: null | ExportType = null;
  @observable public downloadSettings:
    | null
    | {
        type: ExportType.Onboarding;
        cohortId: string;
      }
    | { type: ExportType.Clustering; cohortId: string }
    | {
        type: ExportType.PhoneTracking;
        cohortId: string;
        horizon: number;
        wave: number;
      }
    | {
        type: ExportType.Participants;
        cohortIds: string[];
        cohortTypes: CohortType[];
      } = null;
  @observable public loading = false;
  @observable public downloadData: null | { filename: string; data: string } =
    null;

  constructor() {
    makeObservable(this);
  }

  @action public download = async () => {
    // Onboarding, Clustering, and AllStudyData share one cohort-picker UI.
    // That UI always tags downloadSettings as ExportType.Onboarding.
    // So these three cases check the shape of downloadSettings, not its
    // type field.
    const cohortId =
      this.downloadSettings && 'cohortId' in this.downloadSettings
        ? this.downloadSettings.cohortId
        : null;

    switch (this.exportType) {
      case ExportType.Onboarding: {
        if (!cohortId) {
          throw new Error('No cohort id set');
        }

        const data = await exportOnboardingData(cohortId);
        runInAction(() => {
          this.downloadData = {
            data: data,
            filename: `onboarding-cohort-${cohortId}.csv`,
          };
        });
        break;
      }
      case ExportType.Clustering: {
        if (!cohortId) {
          throw new Error('No cohort id set');
        }

        const data = await clusteringData(cohortId);
        runInAction(() => {
          this.downloadData = {
            data: data,
            filename: `clustering-cohort-${cohortId}.csv`,
          };
        });
        break;
      }
      case ExportType.AllStudyData: {
        if (!cohortId) {
          throw new Error('No cohort id set');
        }
        const data = await studyData(cohortId);
        runInAction(() => {
          this.downloadData = {
            data: data,
            filename: `study-data-cohort-${cohortId}.csv`,
          };
        });
        break;
      }
      case ExportType.PhoneTracking: {
        if (this.downloadSettings?.type !== ExportType.PhoneTracking) {
          throw new Error('Phone tracking download settings not set');
        }
        const { cohortId, horizon, wave } = this.downloadSettings;

        const data = await phoneTrackingData(cohortId, horizon, wave);
        runInAction(() => {
          this.downloadData = {
            data: data,
            filename: `phone-tracking-cohort-${cohortId}-h${horizon}-w${wave}.csv`,
          };
        });
        break;
      }
      case ExportType.Participants: {
        const settings = this.downloadSettings;
        if (!settings || settings.type !== ExportType.Participants) {
          throw new Error('No participant filter set');
        }

        const data = await exportParticipants({
          cohortIds: settings.cohortIds,
          cohortTypes: settings.cohortTypes,
        });
        runInAction(() => {
          this.downloadData = {
            data: data,
            filename: `participants-export.csv`,
          };
        });
        break;
      }
      default: {
        console.log(`Unknown export type ${this.exportType}`);
        break;
      }
    }
  };

  @action public resetModal = () => {
    this.exportType = null;
    this.downloadSettings = null;
    this.open = false;
  };
}

const { provider, useStore } = setupMobX<ExportStore>();
export const useExportStore = useStore;
export const ExportStoreProvider = provider;
